import {useRef} from 'react';

import {
    Alert,
    SimpleGrid,
} from '@mantine/core';
import {
    useNavigate,
    useParams,
} from 'react-router-dom';
import {I18n} from '@aws-amplify/core';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';

import {useBankAccounts} from '../hooks/useBankAccounts';
import {useBankAccountsFilter} from '../hooks/useBankAccountsFilter';
import {useBankAccountsSearch} from '../hooks/useBankAccountsSearch';
import {useVerifiedPermissions} from '../../../hooks/useVerifiedPermissions';
import {rbacActions} from '../../../config/rbac/actions';
import {BankAccount} from '../types';
import {UseBankAccountsSelectionResult} from '../hooks/useBankAccountsSelection';

// FNP-918: migrated from a hand-rolled Mantine <Table> (TableHeaderRow +
// TableBody + RowSelectionDataCell + the app-local PaginationControls) to the
// shared MantineDataTable.
//
// The hand-rolled table was not broken — it rendered rows and supported
// selection — but it structurally could not have sorting, token pagination, or
// the loading/empty/error states, because all three come from the shared
// component. Five checklist gaps close by changing this one component (§3z).
//
// ⚠️ MONEY-PATH ADJACENT. Bank accounts are payment instruments. This component
// was changed for LIST/TABLE compliance only: no tokenization, no Stripe
// Elements, and no create/update payload construction was touched. The edit
// drawer this table used to open inline is gone — editing goes through the
// customer-scoped route (`/customers/:id/bank-accounts/:bankAccountId`), which
// already wraps the form in <Elements>. Opening the form from a table row
// bypassed that wrapper.
//
// ⚠️ `src/components/PaginationControls` and `src/hooks/usePagination` are NOT
// deleted here. Other modules still import them; they come out in a later wave
// once every module is migrated.

interface RecordsTableProps {
    /**
     * Selection is read by IndexPage and passed down, because the delete
     * button lives in the page HEADER — a sibling, not a child of this table.
     * The state itself is the Redux bank-accounts.selection slice (FNP-951);
     * taking it as a prop keeps this component store-free and testable.
     */
    selection?: UseBankAccountsSelectionResult;
}

export const RecordsTable = ({selection}: RecordsTableProps) => {

    const {allowedActions} = useVerifiedPermissions();
    const navigate = useNavigate();

    // Bank accounts are customer-scoped: this table only ever renders inside
    // `/customers/:id/...`. Both param names are accepted because the route
    // tree uses `:id` while some callers pass `:customerId`. The previous
    // version THREW when neither was present, which turns a routing mistake
    // into a blank error boundary; the query is gated on the id instead, so a
    // missing param renders the ordinary empty state.
    const params = useParams();
    const customerId = params.customerId || params.id;

    const {filters} = useBankAccountsFilter();

    const {
        columns,
        // The loaded window BEFORE filtering — the denominator the footer's
        // "N of M loaded" claim is measured against.
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingMore,
        nextToken,
        fetchRecords,
    } = useBankAccounts(customerId ?? '', filters);

    const {
        keyword,
        handleFilterByKeyword,
    } = useBankAccountsSearch();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // The select column is HIDDEN, not disabled, when the action is not
    // permitted (§2, §10). Gated on the same action the delete button is gated
    // on — a checkbox with no permitted batch action behind it is an affordance
    // that does nothing.
    const canSelect = !!selection
        && allowedActions.includes(rbacActions.DeleteBankAccount);

    const selectedRecords = canSelect
        ? visibleItems.filter(item => selection.selection[item.id])
        : undefined;

    // TWO empty states that mean OPPOSITE things (§3). `noRecordsText` is a
    // single string and cannot express both: "no bank accounts" for a search
    // that matched nothing sends an operator hunting for a bug.
    const noRecordsText = keyword.trim() !== ''
        ? I18n.get('No bank accounts match this search')
        : I18n.get('No bank accounts on file for this customer');

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            {/*
              * TableSearchForm, not SimpleTableBatchAction. The latter REQUIRES
              * `limit` + `handleItemsPerPageChange` and renders the rows-per-page
              * dropdown, which token pagination cannot honour — there is no page
              * to resize and no total to divide (§9). The previous table passed
              * `limit={10}` and a no-op handler to satisfy it, so the dropdown
              * rendered and did nothing.
              *
              * TableSearchForm is also the CLEARABLE one: its clear control
              * resets both the input and the module-scoped keyword store, so the
              * list cannot stay narrowed while the box looks empty.
              */}
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert
                    color='red'
                    title={I18n.get('Could not load bank accounts')}
                >
                    {I18n.get(
                        'The billing API did not return the bank account list. Try '
                        + 'Reload; if it persists the API may be unavailable in this '
                        + 'environment.',
                    )}
                </Alert>
            )}

            <MantineDataTable<BankAccount>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far — the
                // same scope the keyword filter works on.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={canSelect
                    ? (records: BankAccount[]) =>
                        selection.handleSelectAllRows(true, records)
                    : undefined}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                // Row click navigates to the detail page (§3). The customer id
                // is part of the path because a bank account is only
                // addressable within its customer.
                onRowClick={({record}: {record: BankAccount}) => {
                    if (customerId) {
                        navigate(`/customers/${encodeURIComponent(customerId)}`
                            + `/bank-accounts/${encodeURIComponent(record.id)}`);
                    }
                }}
            />

            {/*
              * A SIBLING of the table, not a table prop: the table renders rows,
              * the footer answers "have I seen everything?" (§9).
              *
              * `loadedCount` is the rows in the browser — NEVER a dataset total.
              * `filterTerm` must be passed whenever a keyword is active, or a
              * filtered view silently reports the unfiltered state.
              */}
            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'bank account'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};
